import { useTranslation } from 'react-i18next';
import { getSubjectFromToken } from '../lib/auth';
import { getDefaultTenantId } from '../lib/cpClient';
import { portalHeaderTenantText, resolveQueryTenant } from '../features/iot/tenantLabelHonesty';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';
import { AppSwitcher } from './AppSwitcher';
import { LanguageToggle } from './LanguageToggle';
import { NotificationBell } from './NotificationBell';
import { StatusPill } from './StatusPill';

export function Header() {
  const { t } = useTranslation();
  const { token, selectedTenant, signOut, setTenant, isAuthenticated } = useAuthStore();
  const { setShowLoginModal, toggleSidebar } = useUIStore();

  const userSubject = token ? getSubjectFromToken(token) : null;
  const runtimeTenant = getDefaultTenantId();
  const queryTenant = resolveQueryTenant({
    selectedTenant,
    hostedTenant: runtimeTenant,
  });
  const activeTenant = portalHeaderTenantText(queryTenant);
  const tenantOptions = Array.from(new Set([selectedTenant, runtimeTenant].filter(Boolean))) as string[];
  const showTenantSelect = tenantOptions.length > 1;

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Toggle sidebar"
          >
            <span className="text-lg">☰</span>
          </button>

          <div className="flex items-center space-x-2">
            <h1 className="text-lg font-semibold text-gray-900">
              DevCockpit
            </h1>
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
              {activeTenant}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          <StatusPill />

          {isAuthenticated && (
            <>
              <div className="flex items-center gap-2">
                <label htmlFor="tenant-select" className="text-sm text-gray-600">
                  {t('tenant.label')}:
                </label>
                {showTenantSelect ? (
                  <select
                    id="tenant-select"
                    value={activeTenant}
                    onChange={(e) => setTenant(e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                  >
                    {tenantOptions.map((tenant) => (
                      <option key={tenant} value={tenant}>
                        {tenant}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="text-sm border border-gray-200 rounded px-2 py-1 bg-gray-50 text-gray-700">
                    {activeTenant}
                  </span>
                )}
              </div>
              <AppSwitcher />
            </>
          )}

          <LanguageToggle />

          {isAuthenticated && <NotificationBell />}

          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                {userSubject && (
                  <span className="text-sm text-gray-600">
                    {userSubject}
                  </span>
                )}
                <button
                  onClick={signOut}
                  className="btn-secondary px-3 py-1.5 text-sm"
                >
                  {t('auth.signOut')}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowLoginModal(true)}
                className="btn-primary px-4 py-2 text-sm"
              >
                {t('auth.signIn')}
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
